import React from 'react';
import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitDropDown from '../../../../shared/UIKITComponents/DropDown';
import UIKitModal from '../../../../shared/UIKITComponents/Modal';
import { OptionValue } from '../../../../services/types/Misc';

interface SelectTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (documentTemplateId: number) => void;
  templateOptions: OptionValue[];
}

interface FormValues {
  documentTypeId?: number;
}

const SelectTemplateModal = ({
  isOpen,
  onClose,
  onSave,
  templateOptions,
}: SelectTemplateModalProps) => {
  const { t } = useTranslation();

  const formik = useFormik<FormValues>({
    initialValues: {
      documentTypeId: undefined,
    },
    validationSchema: Yup.object({
      documentTypeId: Yup.number().required(t('required')).min(1, t('required')),
    }),
    validateOnChange: true,
    onSubmit: (values, { resetForm }) => {
      if (values.documentTypeId) {
        onSave(values.documentTypeId);
        resetForm();
        onClose();
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <UIKitModal
      open={isOpen}
      onClose={handleClose}
      title={t('createFromTemplate')}
      size="sm"
      actions={
        <>
          <UIKitButton text={t('cancel')} variant="link" onClick={handleClose} color="secondary" />
          <UIKitButton
            text={t('save')}
            variant="flat"
            color="primary"
            disabled={!formik.isValid}
            onClick={formik.submitForm}
          />
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container>
          <Grid size={12}>
            <UIKitDropDown
              elements={templateOptions}
              value={formik.values.documentTypeId || null}
              onChange={(value) => formik.setFieldValue('documentTypeId', value)}
              label={t('selectTemplate')}
              error={formik.touched.documentTypeId && !!formik.errors.documentTypeId}
              helperText={formik.errors.documentTypeId as string}
              fullWidth
            />
          </Grid>
        </Grid>
      </form>
    </UIKitModal>
  );
};

export default SelectTemplateModal;
